import {
  Button,
  Card,
  DataTable,
  EmptyState,
  Icon,
  Text,
  useToast,
  type DataTableColumn,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { ConfirmDialog } from '../../../app/ConfirmDialog';
import { errorMessage } from '../../../lib/errors';
import { formatDate } from '../../../lib/format';
import { useColumnSort, useSortMenu } from '../../../lib/useColumnSort';
import { useSession } from '../../identity/session';
import { useDeleteFeeding, useFeedings, type Feeding } from '../api';
import { FeedingDialog } from '../components/FeedingDialog';
import { isFiltered, RecordToolbar, useRecordFilter } from '../components/RecordFilters';
import { PageActions } from '../../../app/PageActions';
import { useRowMenu } from '../../../app/rowActions';
import { formatAmount } from '../format';
import styles from './RecordPages.module.css';

/** All feedings, filterable (FED-005). */
export function FeedingsPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { can } = useSession();
  const rowMenu = useRowMenu();
  const [filter, setFilter, resetFilter] = useRecordFilter();
  const columnSort = useColumnSort();
  const [recording, setRecording] = useState(false);
  const [editing, setEditing] = useState<Feeding | null>(null);
  const [deleting, setDeleting] = useState<Feeding | null>(null);
  // Paged list: the server sorts, so "Weitere laden" continues in order (#188).
  const list = useFeedings({ ...filter, ...(columnSort.value ? { sort: columnSort.value } : {}) });
  const remove = useDeleteFeeding();
  const rows = list.data?.pages.flatMap((p) => p.items) ?? [];
  const write = can('feedings.write');

  const columns: DataTableColumn<Feeding>[] = [
    {
      key: 'date',
      header: t('apiary.feedings.columns.date'),
      cell: (r) => formatDate(r.fed_on),
      sortValue: (r) => r.fed_on,
      width: '8rem',
    },
    {
      key: 'type',
      header: t('apiary.feedings.columns.type'),
      primary: true,
      cell: (r) => r.feed_type_name,
      sortValue: (r) => r.feed_type_name,
    },
    {
      key: 'colony',
      header: t('apiary.feedings.columns.colony'),
      cell: (r) => <Link to={`/voelker/${r.colony.id}`}>{r.colony.name}</Link>,
      sortValue: (r) => r.colony.name,
    },
    {
      key: 'location',
      header: t('apiary.feedings.columns.location'),
      cell: (r) => r.location?.name ?? '–',
      sortValue: (r) => r.location?.name,
    },
    {
      key: 'amount',
      header: t('apiary.feedings.columns.amount'),
      align: 'end',
      cell: (r) => `${formatAmount(r.amount)} ${r.unit}`,
      sortValue: (r) => r.amount,
    },
    {
      key: 'sugar',
      header: t('apiary.feedings.columns.sugar'),
      align: 'end',
      cell: (r) => (r.sugar_equivalent != null ? `${formatAmount(r.sugar_equivalent, 1)} kg` : '–'),
      sortValue: (r) => r.sugar_equivalent,
    },
    {
      key: 'purpose',
      header: t('apiary.feedings.columns.purpose'),
      cell: (r) => (r.purpose ? t(`apiary.purpose.${r.purpose}`) : '–'),
      sortValue: (r) => (r.purpose ? t(`apiary.purpose.${r.purpose}`) : null),
    },
  ];

  const sortMenu = useSortMenu(columns, rows, columnSort);

  return (
    <div className={styles.page}>
      {write && (
        <PageActions>
          <Button iconStart={<Icon name="plus" size="sm" />} onClick={() => setRecording(true)}>
            {t('apiary.feedings.record')}
          </Button>
        </PageActions>
      )}
      <RecordToolbar value={filter} onChange={setFilter} onReset={resetFilter} sort={sortMenu} />
      <Card padding="none" className={styles.card}>
        {list.error && (
          <Text tone="danger" role="alert" style={{ padding: 'var(--space-5)' }}>
            {errorMessage(list.error)}
          </Text>
        )}
        <DataTable
          caption={t('apiary.feedings.caption')}
          rows={rows}
          rowKey={(r) => r.id}
          {...columnSort}
          presorted
          loading={list.isPending}
          columns={columns}
          {...(write
            ? {
                rowActions: rowMenu.table<Feeding>(
                  (r) => `${r.feed_type_name}, ${r.colony.name}`,
                  (r) => [rowMenu.edit(() => setEditing(r)), rowMenu.remove(() => setDeleting(r))],
                ),
              }
            : {})}
          empty={
            isFiltered(filter) ? (
              <EmptyState
                title={t('common.noResults')}
                actions={
                  <Button variant="secondary" onClick={resetFilter}>
                    {t('common.resetFilters')}
                  </Button>
                }
              />
            ) : (
              <EmptyState icon="droplet" title={t('apiary.feedings.emptyTitle')} />
            )
          }
        />
        {list.hasNextPage && (
          <div className={styles.more}>
            <Button
              variant="secondary"
              loading={list.isFetchingNextPage}
              onClick={() => void list.fetchNextPage()}
            >
              {t('apiary.loadMore')}
            </Button>
          </div>
        )}
      </Card>
      {recording && <FeedingDialog open onClose={() => setRecording(false)} />}
      {editing && <FeedingDialog open feeding={editing} onClose={() => setEditing(null)} />}
      <ConfirmDialog
        open={deleting !== null}
        onClose={() => {
          remove.reset();
          setDeleting(null);
        }}
        title={t('apiary.feeding.deleteTitle')}
        description={t('apiary.feeding.deleteText')}
        confirmLabel={t('common.delete')}
        pending={remove.isPending}
        error={remove.error ? errorMessage(remove.error) : undefined}
        onConfirm={() =>
          deleting &&
          remove.mutate(deleting.id, {
            onSuccess: () => {
              toast.show(t('apiary.feeding.deleted'));
              setDeleting(null);
            },
          })
        }
      />
    </div>
  );
}
